import { CheckIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { Arrow } from '../../../icons/SvgIcons';
import { FilterDropdownProps } from './FilterDropdown.types';

export function FilterDropdown({
  placeholder,
  options,
  selectedValues,
  onSelectionChange,
  className = ''
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionToggle = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onSelectionChange(newSelectedValues);
  };

  const selectedCount = selectedValues.length;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-between w-full
          px-[13px] py-[9px] gap-1
          text-sm font-medium text-gray-800
          bg-white rounded-md
          hover:bg-gray-50 focus:outline-none
          ${
            isOpen
              ? 'ring-2 ring-purple-700 shadow-sm'
              : 'ring-1 ring-gray-250v2'
          }
        `}
      >
        <div className="flex w-full justify-between">
          <span className="text-sm font-medium leading-5">{placeholder}</span>
          {selectedCount > 0 && (
            <div
              className="
              inline-flex items-center justify-center
              min-w-[26px] h-5 px-[10px] py-[2px]
              bg-purple-100 rounded-[10px]
            "
            >
              <span className="text-xs font-medium text-gray-700">
                {selectedCount}
              </span>
            </div>
          )}
        </div>

        <div
          className={`transition-transform duration-300  ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        >
          {Arrow('down')}
        </div>
      </button>

      {isOpen && (
        <div
          className="
    absolute z-10 mt-1 min-w-max w-full
    bg-white border border-gray-200 rounded-md shadow-lg
    max-h-80 overflow-auto
  "
        >
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <div
                key={option.value}
                onClick={() => handleOptionToggle(option.value)}
                className="
                  flex items-center gap-2
                  px-4 py-2 cursor-pointer
                  hover:bg-gray-50
                "
              >
                <div
                  className={`
                  w-4 h-4 rounded border
                  flex items-center justify-center
                  ${
                    isSelected
                      ? 'bg-purple-500 border-purple-500'
                      : 'bg-white border-gray-200'
                  }
                `}
                >
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>

                <span className="text-sm font-normal leading-5 text-gray-800 whitespace-nowrap">
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
